/* Серверный источник шаблонного «Баланса»: движок рисует те же карточки тарифов, сроки, листы и кнопку, данные и команды — отсюда.
   Оплата только тем способом, который сервер объявил (dev_free); исход команды до ответа считается неизвестным и хранится до проверки. */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { CommerceCommand, CommercePlan, CommerceResult, CommerceSubscription, PlanPeriod } from '../../api/commerce';
import type { Balance, Me } from '../../api/types';
import { uiText } from '../../i18n';
import { applyReferralState } from '../../bridge/partner-ref';
import { tokenDecimal } from './token-decimal';
import {
  clearSubscriptionIntent,
  readSubscriptionIntent,
  saveSubscriptionIntent,
  subscriptionError,
  type SubscriptionIntent,
} from './subscription-model';

export interface BalancePeriod {
  n: number;
  stars: number;
  auto: boolean;
  /** Сервер разрешил бесплатную dev-оплату этого срока */
  free: boolean;
  modes: string[];
  unavailableReason?: string | null;
  /** Цена срока картой через ЮKassa, ₽ (есть, когда сервер объявил способ card) */
  rub?: number | null;
}

export interface BalancePlan {
  id: string;
  name: string;
  /** Звёзды за месяц: ими движок сравнивает тарифы */
  price: number;
  tokens: number;
  voices: number;
  revision: number;
  priceVersion: string;
  dev: boolean;
  giftModes: string[];
  periods: BalancePeriod[];
}

export interface BalancePack {
  priceVersion: string;
  originalStars: number | null;
  discountPct: number;
  id: string;
  c: number;
  p: number;
  revision: number;
  free: boolean;
  modes: string[];
  /** Цена картой (Tribute) в рублях; null — только звёзды */
  rub: number | null;
}

export interface BalanceSub {
  tokens: number | null;
  voices: number | null;
  plan: string;
  name: string;
  via: string;
  till: number;
  auto: boolean;
  per: number;
  next: string | null;
  nextPer: number;
  actions: NonNullable<CommerceSubscription['actions']>;
}

export interface BalanceSnapshot {
  plans: BalancePlan[];
  freeVoices: number;
  periodDays: number;
  sub: BalanceSub | null;
  scheduledInvoice?: CommerceSubscription['scheduled_invoice'];
  scheduleDelivery?: CommerceSubscription['schedule_delivery'];
  /** Закончившаяся подписка: дата для подписи «закончилась» */
  endedAt: number | null;
  packs: BalancePack[];
  packsAvailable: boolean;
  balance: Balance;
  me: Me;
  intent: SubscriptionIntent | null;
  storageBlocked: boolean;
}

export interface LedgerRow {
  id: number;
  a: string;
  credit: boolean;
  at: number;
  canceled: boolean;
}

const NO_ACTIONS = { cancel: false, resume: false, downgrade: false, extend: false, upgrade: false };
/** Коды, после которых команда точно не выполнена: намерение можно снять */
const DEFINITIVE = [
  'subscription_already_active',
  'subscription_not_found',
  'subscription_resume_unavailable',
  'gift_period_active',
  'subscription_schedule_exists',
  'subscription_schedule_invalid',
  'subscription_schedule_changed',
  'subscription_schedule_not_due',
  'subscription_schedule_canceled',
  'subscription_downgrade_invalid',
  'subscription_required',
  'commerce_revision_conflict',
  'price_changed',
  'commerce_payment_provider_unconfigured',
  'product_unavailable',
  'commerce_period_invalid',
  'product_not_found',
  'price_confirmation_required',
  'commerce_input_invalid',
  'referral_discount_reserved',
  'nothing_to_buy',
  'period_unavailable',
  'subscription_downgrade_required',
  'clone_slots_product_unconfigured',
  'gift_share_forbidden',
  'material_bot_unavailable',
  'gift_recipient_unavailable',
  'promo_not_found',
  'promo_expired',
  'promo_not_started',
  'promo_used',
  'promo_limit',
  'promo_own',
  'promo_after_purchase',
  'promo_paused',
  'partner_already_bound',
  'gift_recipient_invalid',
  'gift_recipient_busy',
  'gift_wish_invalid',
  'gift_not_found',
  'gift_not_yours',
  'gift_already_activated',
];

const period = (p: PlanPeriod): BalancePeriod => ({
  n: p.months,
  stars: p.stars,
  auto: p.auto_renew,
  free: !!p.checkout_modes?.includes('dev_free'),
  modes: p.checkout_modes ?? [],
  unavailableReason: p.unavailable_reason,
  rub: p.rub ?? null,
});

function plan(p: CommercePlan): BalancePlan {
  const periods = [...p.periods].sort((a, b) => a.months - b.months).map(period);
  return {
    id: p.id,
    name: p.name,
    price: periods.find(x => x.n === 1)?.stars ?? periods[0]?.stars ?? 0,
    tokens: p.circle_per_month,
    voices: p.voices_limit,
    revision: p.revision,
    priceVersion: p.price_version,
    dev: p.dev_only,
    giftModes: (p.gift_checkout_modes ?? []).filter(mode => mode !== 'dev_free' || p.dev_only),
    periods: p.dev_only ? periods : periods.map(x => ({ ...x, free: false, modes: x.modes.filter(mode => mode !== 'dev_free') })),
  };
}

function sub(s: CommerceSubscription | null, plans: BalancePlan[]): BalanceSub | null {
  if (s?.state !== 'active') return null;
  return {
    plan: s.plan_id,
    tokens: s.circle_per_month ?? null,
    voices: s.voices_limit ?? null,
    name: s.name ?? plans.find(p => p.id === s.plan_id)?.name ?? s.plan_id,
    via: s.via,
    till: Date.parse(s.paid_until),
    auto: s.auto_renew,
    per: s.months || 1,
    next: s.next?.plan_id ?? null,
    nextPer: s.next?.months ?? 1,
    actions: s.actions ?? NO_ACTIONS,
  };
}

function publish(me: Me) {
  applyReferralState(me);
  window.BotServerMe = me;
  document.dispatchEvent(new CustomEvent('steos:account-updated', { detail: me }));
}

/** Владелец снимка: смена аккаунта между загрузками — снимок не принимается */
let owner: number | null = null;

export const balanceServer = {
  gift: endpoints.commerce.gift,
  async load(): Promise<BalanceSnapshot> {
    const api = endpoints.commerce;
    const [catalog, current, balance, me, packs] = await Promise.all([
      api.plans(),
      api.subscription(),
      endpoints.balance(),
      endpoints.me(),
      api.packs(),
    ]);
    if (owner !== null && owner !== me.user.id) throw new Error('owner_changed');
    owner = me.user.id;
    publish(me);
    const plans = catalog.items.map(plan).sort((a, b) => a.tokens - b.tokens);
    let intent: SubscriptionIntent | null = null,
      storageBlocked = false;
    try {
      intent = readSubscriptionIntent(me.user.id);
    } catch {
      storageBlocked = true;
    }
    return {
      plans,
      freeVoices: catalog.free.voices_limit,
      periodDays: catalog.period_days,
      sub: sub(current, plans),
      scheduledInvoice: current?.scheduled_invoice,
      scheduleDelivery: current?.schedule_delivery,
      endedAt: current?.state === 'expired' ? Date.parse(current.paid_until) : null,
      packs: packs.items.map(k => ({
        id: k.id,
        priceVersion: k.price_version ?? '',
        originalStars: k.periods[0]?.original_stars ?? null,
        discountPct: k.periods[0]?.discount_pct ?? 0,
        c: k.circle,
        p: k.periods[0]?.stars ?? 0,
        revision: k.revision,
        modes: k.periods[0]?.checkout_modes ?? [],
        rub: k.periods[0]?.rub ?? null,
        free: packs.available && k.dev_only && k.periods.some(x => x.checkout_modes?.includes('dev_free')),
      })),
      packsAvailable: packs.available,
      balance,
      me,
      intent,
      storageBlocked,
    };
  },
  /** Команда подписки или пакета: намерение пишется до запроса (страницу могут закрыть до ответа); точный отказ снимает его, неизвестный исход оставляет для проверки */
  async cancelPayment(snap: BalanceSnapshot): Promise<CommerceResult> {
    const intent = snap.intent;
    if (!intent?.payment || (!intent.payment.can_cancel && !intent.cancelRequested)) throw new Error('subscription_intent_invalid');
    const saved = readSubscriptionIntent(snap.me.user.id);
    if (!saved || saved.key !== intent.key || saved.payment?.invoice_id !== intent.payment.invoice_id)
      throw new Error('subscription_intent_invalid');
    const next = { ...saved, cancelRequested: true, cancelKey: saved.cancelKey ?? uuid() };
    saveSubscriptionIntent(next);
    return balanceServer.run({ ...snap, intent: next }, null);
  },
  async run(snap: BalanceSnapshot, command: CommerceCommand | null): Promise<CommerceResult> {
    if (command && (snap.intent || snap.storageBlocked)) throw new Error('subscription_intent_invalid');
    const value: SubscriptionIntent | null = command ? { owner: snap.me.user.id, key: uuid(), command } : snap.intent;
    if (!value || value.owner !== snap.me.user.id) throw new Error('subscription_intent_invalid');
    const me = await endpoints.me();
    if (me.user.id !== value.owner) throw new Error('subscription_intent_invalid');
    if (command && readSubscriptionIntent(value.owner)) throw new Error('subscription_intent_invalid');
    saveSubscriptionIntent({ ...value, unknown: true });
    try {
      const result =
        value.cancelRequested && value.payment && value.cancelKey
          ? { ...value.payment, ...(await endpoints.commerce.cancelPayment(value.payment.invoice_id, value.cancelKey)) }
          : value.payment
            ? { ...value.payment, ...(await endpoints.commerce.payment(value.payment.invoice_id)) }
            : await endpoints.commerce.command(value.command, value.key);
      if (!result.invoice_id && ['plan', 'downgrade'].includes(value.command.kind) && result.outcome === 'canceled') {
        clearSubscriptionIntent(value.owner);
        throw new ApiError(409, 'subscription_schedule_canceled', 'Запланированное продление отменено');
      }
      if (result.invoice_id && result.error_code === 'payment_requires_review') {
        saveSubscriptionIntent({ ...value, unknown: true, payment: { ...result, can_cancel: false, invoice_id: result.invoice_id } });
        throw new Error('payment_requires_review');
      }
      if (result.invoice_id && result.state !== 'paid' && result.outcome !== 'applied') {
        if (['failed', 'expired', 'canceled'].includes(result.state ?? '')) {
          clearSubscriptionIntent(value.owner);
          throw new Error('payment_terminal_' + result.state);
        }
        saveSubscriptionIntent({ ...value, unknown: true, payment: { ...result, invoice_id: result.invoice_id } });
        return result;
      }
      if (value.command.kind === 'gift_share' && result.state === 'pending') throw new Error('gift_share_pending');
      clearSubscriptionIntent(value.owner);
      return result;
    } catch (error) {
      if (
        value.cancelRequested &&
        error instanceof ApiError &&
        ['payment_precheckout_pending', 'payment_already_paid', 'payment_cancel_unavailable'].includes(error.code)
      ) {
        const original = { ...value };
        delete original.cancelRequested;
        delete original.cancelKey;
        saveSubscriptionIntent({
          ...original,
          unknown: true,
          payment: original.payment ? { ...original.payment, can_cancel: false } : undefined,
        });
      }
      if (value.command.kind === 'promo' && error instanceof ApiError && error.code === 'product_not_found') {
        if (!value.unknown) clearSubscriptionIntent(value.owner);
        throw new ApiError(error.status, 'promo_not_found', 'Такого промокода нет');
      }
      if (!value.unknown && error instanceof ApiError && DEFINITIVE.includes(error.code)) clearSubscriptionIntent(value.owner);
      throw error;
    }
  },
  async ledger(before?: number): Promise<{ items: LedgerRow[]; next: number | null }> {
    const page = await endpoints.finance.overview(before);
    if (!page.capabilities.legacy_ledger) throw new Error('token_ledger_unavailable');
    return {
      items: page.ledger.map(e => ({
        id: e.id,
        a: tokenDecimal(e.amount_tokens).replace(/^-/, ''),
        credit: e.direction === 'credit',
        at: e.timestamp_unix * 1000,
        canceled: e.canceled,
      })),
      next: page.next_before_id,
    };
  },
  error(error: unknown) {
    if (
      error instanceof ApiError &&
      ['payment_precheckout_pending', 'payment_already_paid', 'payment_cancel_unavailable'].includes(error.code)
    )
      return uiText('Счёт уже подтверждается или недоступен для отмены. Проверьте результат оплаты.');
    if (error instanceof Error && error.message === 'payment_requires_review')
      return uiText(
        'Оплата требует проверки. Сервер сохранил платёж; права пока не подтверждены. Не оплачивайте повторно — проверьте результат позже или обратитесь в поддержку.',
      );
    if (error instanceof Error && error.message === 'payment_terminal_canceled')
      return uiText('Счёт отменён. Подписка и Circle по нему не начислены. Можно выбрать новую оплату.');
    if (error instanceof Error && error.message.startsWith('payment_terminal_'))
      return uiText('Оплата не завершена. Деньги и Circle не подтверждены; можно начать новую оплату.');
    if (error instanceof Error && error.message === 'gift_share_pending')
      return uiText('Открытка готовится. Нажмите «Проверить», чтобы получить результат прежнего запроса.');
    if (error instanceof Error && error.message === 'subscription_intent_invalid')
      return uiText('Не удалось восстановить операцию. Проверьте подписку перед новой покупкой.');
    return subscriptionError(error);
  },
};

export type BalanceServer = typeof balanceServer;
